import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconUsersGroup } from '@tabler/icons-react';
import { BetaSchemaForm, ProFormColumnsType } from '@ant-design/pro-components';
import { CrudMode, renderMultiListToTags, SelectOptions } from '@ohpen/enso-ui';
import { Form } from 'antd';

import { TabProps } from '../interfaces';
import { FirmGetById } from '@pmp/api';
import { CrudFormCard } from './CrudFormCard';
import { OPERATION_ORDER, operationToFieldTypeMap } from '../utils';

export const Packaging: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const watchedOperations = Form.useWatch(['registrationDetails', 'operations'], form);

  const operations: string[] = useMemo(() => {
    if (!watchedOperations) return [];
    return [...watchedOperations].sort((a, b) => OPERATION_ORDER.indexOf(a) - OPERATION_ORDER.indexOf(b));
  }, [watchedOperations]);

  const packagingOptions: SelectOptions = useMemo(() => {
    if (!operations?.length) return [];

    return operations.flatMap(operation => {
      const fieldType = operationToFieldTypeMap[operation];
      if (!fieldType) return [];

      return selectsOptions[fieldType] ?? [];
    });
  }, [operations, selectsOptions]);

  const columns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        title: t('lender.firms.labels.thisFirmIsPackagerFor'),
        dataIndex: ['packaging', 'packagerForFirmIds'],
        valueType: 'select',
        readonly,
        render: renderMultiListToTags,
        fieldProps: {
          mode: 'multiple',
          options: packagingOptions,
        },
        colProps: { span: 12 },
      },
      {
        title: t('lender.firms.labels.packagersCanCreateCasesForThisFirm'),
        dataIndex: ['packaging', 'packagerFirmIds'],
        valueType: 'select',
        readonly,
        render: renderMultiListToTags,
        fieldProps: {
          mode: 'multiple',
          options: packagingOptions,
        },
        colProps: { span: 12 },
      },
    ],
    [readonly, t, packagingOptions],
  );

  return (
    <CrudFormCard icon={<IconUsersGroup />} title={t('lender.firms.labels.packaging')}>
      <BetaSchemaForm<FirmGetById> form={form} layoutType="Embed" columns={columns} submitter={false} />
    </CrudFormCard>
  );
};
